import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import {
  Settings,
  Plus,
  Search,
  ChevronDown,
  ChevronRight,
  Edit2,
  Trash2,
  Power,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Info,
  Eye,
} from 'lucide-react'
import { ruleService } from '../../services/ruleService'
import {
  Rule,
  RuleCategory,
  RequestType,
  REQUEST_TYPE_LABELS,
  WORK_CENTER_LABELS,
  RULE_CATEGORY_LABELS,
} from '../../types'
import { ConfirmDialog, useConfirmDialog } from '../../components/ui/ConfirmDialog'
import { RuleFormModal } from '../../components/rules/RuleFormModal'
import { extractApiErrors } from '../../utils/errorHandler'

export function RulesPage() {
  const navigate = useNavigate()
  const [rules, setRules] = useState<Rule[]>([])
  const [groupedRules, setGroupedRules] = useState<Record<string, Rule[]>>({})
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterCategory, setFilterCategory] = useState<RuleCategory | ''>('')
  const [filterRequestType, setFilterRequestType] = useState<RequestType | ''>('')
  const [filterActive, setFilterActive] = useState<boolean | ''>('')
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const [showFormModal, setShowFormModal] = useState(false)
  const [editingRule, setEditingRule] = useState<Rule | null>(null)
  const [seeding, setSeeding] = useState(false)

  const confirmDialog = useConfirmDialog()

  const handleView = (rule: Rule) => {
    navigate(`/configuration/rules/${rule.id}`)
  }

  useEffect(() => {
    loadRules()
  }, [])

  const loadRules = async () => {
    try {
      setLoading(true)
      const response = await ruleService.getAllGrouped()
      setRules(response.rules)
      setGroupedRules(response.grouped || {})
      // Expand all groups by default
      setExpandedGroups(new Set(Object.keys(response.grouped || {})))
    } catch (error) {
      toast.error('Error al cargar las reglas')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const handleSeedDefaults = async () => {
    const confirmed = await confirmDialog.confirm({
      title: 'Cargar reglas por defecto',
      message: 'Se crearán las reglas predeterminadas del sistema. Las reglas existentes con el mismo código no se duplicarán.',
      confirmText: 'Cargar',
      cancelText: 'Cancelar',
      variant: 'primary',
    })

    if (!confirmed) return

    try {
      setSeeding(true)
      const result = await ruleService.seedDefaults()
      toast.success(`${result.created} reglas creadas correctamente`)
      loadRules()
    } catch (error: unknown) {
      const { message, details } = extractApiErrors(error)
      toast.error(message, {
        description: details.length > 0
          ? details.map(d => d.message).join('. ')
          : undefined,
      })
    } finally {
      setSeeding(false)
    }
  }

  const handleToggleActive = async (rule: Rule) => {
    try {
      const updated = await ruleService.toggleActive(rule.id)
      toast.success(`Regla ${updated.isActive ? 'activada' : 'desactivada'}`)
      loadRules()
    } catch (error: unknown) {
      const { message, details } = extractApiErrors(error)
      toast.error(message, {
        description: details.length > 0
          ? details.map(d => d.message).join('. ')
          : undefined,
      })
    }
  }

  const handleDelete = async (rule: Rule) => {
    const confirmed = await confirmDialog.confirm({
      title: 'Eliminar regla',
      message: `¿Estás seguro de eliminar la regla "${rule.name}"? Esta acción no se puede deshacer.`,
      confirmText: 'Eliminar',
      cancelText: 'Cancelar',
      variant: 'danger',
    })

    if (!confirmed) return

    try {
      await ruleService.delete(rule.id)
      toast.success('Regla eliminada correctamente')
      loadRules()
    } catch (error: unknown) {
      const { message, details } = extractApiErrors(error)
      toast.error(message, {
        description: details.length > 0
          ? details.map(d => d.message).join('. ')
          : undefined,
      })
    }
  }

  const handleEdit = (rule: Rule) => {
    setEditingRule(rule)
    setShowFormModal(true)
  }

  const handleCreate = () => {
    setEditingRule(null)
    setShowFormModal(true)
  }

  const handleFormSuccess = () => {
    setShowFormModal(false)
    setEditingRule(null)
    loadRules()
  }

  const toggleGroup = (group: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(group)) {
        newSet.delete(group)
      } else {
        newSet.add(group)
      }
      return newSet
    })
  }

  // Filter rules
  const filteredGroupedRules = Object.entries(groupedRules).reduce((acc, [key, groupRules]) => {
    const filtered = groupRules.filter((rule) => {
      if (searchTerm) {
        const search = searchTerm.toLowerCase()
        if (
          !rule.name.toLowerCase().includes(search) &&
          !rule.code.toLowerCase().includes(search) &&
          !(rule.description?.toLowerCase().includes(search))
        ) {
          return false
        }
      }
      if (filterCategory && rule.category !== filterCategory) return false
      if (filterRequestType && rule.requestType !== filterRequestType) return false
      if (filterActive !== '' && rule.isActive !== filterActive) return false
      return true
    })

    if (filtered.length > 0) {
      acc[key] = filtered
    }
    return acc
  }, {} as Record<string, Rule[]>)

  const formatRuleValues = (rule: Rule) => {
    const parts: string[] = []
    const v = rule.values

    if (v.minDays !== undefined) parts.push(`Min: ${v.minDays} días`)
    if (v.maxDays !== undefined) parts.push(`Max: ${v.maxDays} días`)
    if (v.exactDays !== undefined) parts.push(`Exacto: ${v.exactDays} días`)
    if (v.maxPercent !== undefined) parts.push(`${v.maxPercent}% máx`)
    if (v.minWorkers !== undefined) parts.push(`Min ${v.minWorkers} trab.`)
    if (v.maxPerPosition !== undefined) parts.push(`Max ${v.maxPerPosition}/puesto`)
    if (v.maxConcurrent !== undefined) parts.push(`Max ${v.maxConcurrent} conc.`)
    if (v.minDaysAt5Shifts !== undefined) parts.push(`Min ${v.minDaysAt5Shifts} días 5R`)
    if (v.minDaysLast10Years !== undefined) parts.push(`Min ${v.minDaysLast10Years} últ. 10 años`)
    if (v.month !== undefined && v.day !== undefined) parts.push(`Hasta ${v.day}/${v.month}`)
    if (v.maxPerTurnPosition !== undefined) parts.push(`Max ${v.maxPerTurnPosition}/turno`)
    if (v.maxPerDay !== undefined) parts.push(`Max ${v.maxPerDay}/día`)
    if (v.blockedPeriodTypes?.length) parts.push(`Bloq: ${v.blockedPeriodTypes.length} periodos`)
    if (v.requiresAt5Shifts) parts.push('Req. 5R')
    if (v.notEnjoyed2Years) parts.push('No 2 años ant.')

    return parts.join(' | ') || '-'
  }

  const getRequestTypeLabel = (type: string | null) => {
    if (!type || type === 'GENERAL') return 'General (Todas)'
    return REQUEST_TYPE_LABELS[type as RequestType] || type
  }

  return (
    <div className="animate-slide-in">
      <div className="page-header">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="page-title flex items-center gap-2">
              <Settings className="h-6 w-6" />
              <span className="hidden sm:inline">Configuración de Reglas</span>
              <span className="sm:hidden">Reglas</span>
            </h1>
            <p className="page-subtitle">
              Gestiona las reglas de negocio para la validación de solicitudes
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleSeedDefaults}
              disabled={seeding}
              className="btn btn-outline flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${seeding ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Cargar por defecto</span>
              <span className="sm:hidden">Cargar</span>
            </button>
            <button
              onClick={handleCreate}
              className="btn btn-primary flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Nueva Regla</span>
              <span className="sm:hidden">Nueva</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex flex-col gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nombre, código..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10 w-full"
            />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value as RuleCategory | '')}
              className="input text-sm col-span-2 sm:col-span-1"
            >
              <option value="">Todas categorías</option>
              {Object.entries(RULE_CATEGORY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>

            <select
              value={filterRequestType}
              onChange={(e) => setFilterRequestType(e.target.value as RequestType | '')}
              className="input text-sm col-span-2 sm:col-span-1"
            >
              <option value="">Todos los tipos</option>
              {Object.entries(REQUEST_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>

            <select
              value={filterActive === '' ? '' : filterActive ? 'true' : 'false'}
              onChange={(e) =>
                setFilterActive(e.target.value === '' ? '' : e.target.value === 'true')
              }
              className="input text-sm"
            >
              <option value="">Todas</option>
              <option value="true">Activas</option>
              <option value="false">Inactivas</option>
            </select>
          </div>
        </div>
      </div>

      {/* Rules List */}
      {loading ? (
        <div className="card">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        </div>
      ) : Object.keys(filteredGroupedRules).length === 0 ? (
        <div className="card">
          <div className="text-center py-12">
            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No hay reglas configuradas
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              Carga las reglas por defecto o crea una nueva regla
            </p>
            <button
              onClick={handleSeedDefaults}
              className="btn btn-primary"
            >
              Cargar reglas por defecto
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(filteredGroupedRules).map(([groupKey, groupRules]) => (
            <div key={groupKey} className="card overflow-hidden">
              <button
                onClick={() => toggleGroup(groupKey)}
                className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-dark-border hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {expandedGroups.has(groupKey) ? (
                    <ChevronDown className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-gray-500" />
                  )}
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {getRequestTypeLabel(groupKey)}
                  </h3>
                  <span className="badge badge-gray">
                    {groupRules.length} reglas
                  </span>
                </div>
              </button>

              {expandedGroups.has(groupKey) && (
                <div className="divide-y divide-gray-200 dark:divide-dark-border">
                  {groupRules.map((rule) => (
                    <div
                      key={rule.id}
                      className={`p-4 ${!rule.isActive ? 'bg-gray-50 dark:bg-gray-800/50 opacity-60' : ''}`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          {/* Badges - scrollable en móvil */}
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span className="font-mono text-xs bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded flex-shrink-0">
                              {rule.code}
                            </span>
                            <span
                              className={`badge text-xs ${
                                rule.isActive ? 'badge-success' : 'badge-gray'
                              }`}
                            >
                              {rule.isActive ? 'Activa' : 'Inactiva'}
                            </span>
                            <span className="badge badge-outline text-xs hidden sm:inline-flex">
                              {RULE_CATEGORY_LABELS[rule.category]}
                            </span>
                            {rule.workCenter && (
                              <span className="badge badge-info text-xs">
                                {WORK_CENTER_LABELS[rule.workCenter]}
                              </span>
                            )}
                          </div>

                          <h4 className="font-medium text-gray-900 dark:text-white text-sm sm:text-base">
                            {rule.name}
                          </h4>

                          {rule.description && (
                            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                              {rule.description}
                            </p>
                          )}

                          <div className="mt-2 text-xs sm:text-sm">
                            <div className="flex items-start gap-1 text-gray-600 dark:text-gray-300">
                              <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                              <span className="break-words">{formatRuleValues(rule)}</span>
                            </div>
                          </div>

                          {rule.errorMessage && (
                            <div className="flex items-start gap-1 mt-2 text-xs sm:text-sm text-red-600 dark:text-red-400">
                              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                              <span className="break-words">{rule.errorMessage}</span>
                            </div>
                          )}

                          {rule.conditions && Object.keys(rule.conditions).length > 0 && (
                            <div className="flex items-center gap-2 mt-2 text-xs text-gray-500 flex-wrap">
                              <span className="font-medium">Condiciones:</span>
                              {rule.conditions.hasStop !== undefined && (
                                <span className="badge badge-outline text-xs">
                                  {rule.conditions.hasStop ? 'Con parada' : 'Sin parada'}
                                </span>
                              )}
                              {rule.conditions.isExtension !== undefined && (
                                <span className="badge badge-outline text-xs">
                                  {rule.conditions.isExtension ? 'Es prórroga' : 'No prórroga'}
                                </span>
                              )}
                              {rule.conditions.daysRequested !== undefined && (
                                <span className="badge badge-outline text-xs">
                                  {rule.conditions.daysRequested} días
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Botones de acción - horizontal en móvil */}
                        <div className="flex items-center gap-1 sm:gap-2 pt-2 sm:pt-0 border-t sm:border-t-0 border-gray-100 dark:border-slate-700 sm:ml-4">
                          <button
                            onClick={() => handleView(rule)}
                            className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 dark:hover:bg-slate-700 dark:text-gray-400 transition-colors flex-1 sm:flex-initial"
                            title="Ver detalle"
                          >
                            <Eye className="h-4 w-4 mx-auto" />
                          </button>
                          <button
                            onClick={() => handleToggleActive(rule)}
                            className={`p-2 rounded-lg transition-colors flex-1 sm:flex-initial ${
                              rule.isActive
                                ? 'text-success-600 hover:bg-success-50 dark:hover:bg-success-900/20'
                                : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                            title={rule.isActive ? 'Desactivar' : 'Activar'}
                          >
                            <Power className="h-4 w-4 mx-auto" />
                          </button>
                          <button
                            onClick={() => handleEdit(rule)}
                            className="p-2 rounded-lg text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors flex-1 sm:flex-initial"
                            title="Editar"
                          >
                            <Edit2 className="h-4 w-4 mx-auto" />
                          </button>
                          <button
                            onClick={() => handleDelete(rule)}
                            className="p-2 rounded-lg text-error-600 hover:bg-error-50 dark:hover:bg-error-900/20 transition-colors flex-1 sm:flex-initial"
                            title="Eliminar"
                          >
                            <Trash2 className="h-4 w-4 mx-auto" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Stats summary */}
      {!loading && rules.length > 0 && (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="card p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-primary-100 dark:bg-primary-900/30">
              <Settings className="h-6 w-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {rules.length}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Total de reglas
              </p>
            </div>
          </div>

          <div className="card p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-success-100 dark:bg-success-900/30">
              <CheckCircle className="h-6 w-6 text-success-600 dark:text-success-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {rules.filter((r) => r.isActive).length}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Reglas activas
              </p>
            </div>
          </div>

          <div className="card p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-gray-100 dark:bg-gray-700">
              <AlertCircle className="h-6 w-6 text-gray-600 dark:text-gray-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {rules.filter((r) => !r.isActive).length}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Reglas inactivas
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showFormModal && (
        <RuleFormModal
          rule={editingRule}
          onClose={() => {
            setShowFormModal(false)
            setEditingRule(null)
          }}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog {...confirmDialog.dialogProps} />
    </div>
  )
}
